import * as React from 'react'

import usePrefersReducedMotion from '../../hooks/usePrefersReducedMotion'
import { SMALL_ICON_BASE_SRC } from '../../utils/constants'

const SCROLL_BOTTOM_THRESHOLD = 16

interface ScrollToContinueIndicatorProps {
  /**
   * Optional ref to the element whose bottom should be scrolled into view
   * when the indicator is clicked. Defaults to scrolling the window to the
   * bottom of the document.
   */
  scrollTargetRef?: React.RefObject<HTMLElement>;
}

const ScrollToContinueIndicator = ({ scrollTargetRef }: ScrollToContinueIndicatorProps) => {
  const [hasContentBelow, setHasContentBelow] = React.useState(false)
  const prefersReducedMotion = usePrefersReducedMotion()

  const updateVisibility = React.useCallback(() => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop
    const viewportHeight = window.innerHeight
    const documentHeight = Math.max(
      document.documentElement.scrollHeight,
      document.body.scrollHeight
    )
    const distanceFromBottom = documentHeight - (scrollTop + viewportHeight)
    setHasContentBelow(distanceFromBottom > SCROLL_BOTTOM_THRESHOLD)
  }, [])

  React.useEffect(() => {
    updateVisibility()

    window.addEventListener('scroll', updateVisibility, { passive: true })
    window.addEventListener('resize', updateVisibility)

    let resizeObserver: ResizeObserver | null = null
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateVisibility)
      resizeObserver.observe(document.documentElement)
      if (document.body) {
        resizeObserver.observe(document.body)
      }
    }

    return () => {
      window.removeEventListener('scroll', updateVisibility)
      window.removeEventListener('resize', updateVisibility)
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
    }
  }, [updateVisibility])

  const handleClick = () => {
    const behavior: ScrollBehavior = prefersReducedMotion ? 'auto' : 'smooth'
    const target = scrollTargetRef?.current
    if (target) {
      const targetBottom = target.getBoundingClientRect().bottom + window.scrollY
      window.scrollTo({ top: targetBottom, behavior })
      return
    }
    const documentHeight = Math.max(
      document.documentElement.scrollHeight,
      document.body.scrollHeight
    )
    window.scrollTo({ top: documentHeight, behavior })
  }

  return (
    <button
      aria-hidden={!hasContentBelow}
      className={`scroll-to-continue-indicator ${hasContentBelow ? 'visible' : 'hidden'}`}
      onClick={handleClick}
      tabIndex={hasContentBelow ? 0 : -1}
      type="button"
    >
      <span className="scroll-to-continue-indicator__label">Scroll to Continue</span>
      <img alt="" className="scroll-to-continue-indicator__chevron" src={`${SMALL_ICON_BASE_SRC}/chevron-wide-down.svg`} />
    </button>
  )
}

export { ScrollToContinueIndicator }
